/**
 * AI SDR System Main App
 *
 * Main application component that provides:
 * 1. Prompt entry to create/hire an AI SDR
 * 2. Dual-panel training interface for the SDR
 * 3. localStorage persistence for SDR ID
 */

import { UserHeader } from "@client/components/auth/UserHeader";
import { Card } from "@client/components/card/Card";
import { useCreateSDR, useLastCreatedSDR } from "@client/hooks";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { SDRDualPanel } from "../components/sdr-dual-panel";

export const Route = createFileRoute("/")({
	component: SDRApp,
});

// Removed localStorage storage - now using backend requests

type SDRInitialPrompt = {
	button: string;
	persona: string;
};

const SDR_INITIAL_PROMPTS: SDRInitialPrompt[] = [
	{
		button: "Happywood",
		persona:
			"You are an expert sales representative for 'Happywood,' a construction lumber store located at 203 Camden Ave, San Jose, CA. Your goal is to actively consult customers on what lumber they need for their specific construction projects. You demonstrate professionalism by asking detailed questions about their project requirements, recommending the right types of wood, sizes, and quantities. Your expertise in lumber selection and construction applications helps build trust with customers, whether they're professional contractors, DIY enthusiasts, or homeowners. You guide them through material choices based on project type, budget, durability needs, and local building requirements.",
	},
	{
		button: "EcoClean",
		persona:
			"You are an expert SDR for 'EcoClean Solutions,' a premium eco-friendly commercial cleaning service. Your goal is to book consultations with office managers, facility directors, and business owners of companies with 20-200 employees. You will be selling our green cleaning services that use only non-toxic, biodegradable products while maintaining superior cleanliness standards. You emphasize cost savings through reduced sick days, improved employee health, environmental responsibility, and our 100% satisfaction guarantee. Your approach is consultative, focusing on understanding their current cleaning challenges, budget constraints, and sustainability goals.",
	},
	{
		button: "SolarMax",
		persona:
			"You are an expert SDR for 'SolarMax,' a residential solar panel installation company. Your goal is to book in-home consultations with homeowners interested in reducing energy costs and going green. You focus on immediate savings, tax incentives, increased property value, and environmental impact. You qualify leads by discussing current electricity bills, roof condition, and energy usage patterns to demonstrate potential ROI.",
	},
	{
		button: "WealthPath",
		persona:
			"You are an expert SDR for 'WealthPath Advisors,' a boutique investment advisory firm. Your goal is to schedule strategy sessions with professionals earning $100K+ who need personalized wealth management. You focus on retirement planning, tax optimization, and portfolio diversification. You qualify prospects by understanding their financial goals, current investment challenges, and timeline to build trust before introducing our fee-based advisory services.",
	},
];

const EXAMPLE_SDR_PERSONA = SDR_INITIAL_PROMPTS[0].persona;

export default function SDRApp() {
	const [sdrId, setSDRId] = useState<string | null>(null);
	const [persona, setPersona] = useState(EXAMPLE_SDR_PERSONA);
	const [showPrompt, setShowPrompt] = useState(true);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	// Hooks for API operations
	const createSDRMutation = useCreateSDR();
	//const trackPageView = useTrackPageView();
	const lastCreatedSDR = useLastCreatedSDR();

	// Auto-resize textarea
	const autoResize = () => {
		const textarea = textareaRef.current;
		if (textarea) {
			textarea.style.height = 'auto';
			textarea.style.height = `${textarea.scrollHeight}px`;
		}
	};

	useEffect(() => {
		autoResize();
	}, [persona]);

	// Load last created SDR from backend on mount and handle pending persona from query parameter
	useEffect(() => {
		const pendingPersona = localStorage.getItem("pending-persona");

		if (pendingPersona) {
			console.log(
				"🔍 Found pending persona from query parameter:",
				pendingPersona,
			);
			// Set the persona and automatically create SDR
			setPersona(pendingPersona);
			// Clear the pending persona from localStorage
			localStorage.removeItem("pending-persona");
			// Auto-create the SDR
			createSDRWithPersona(pendingPersona);
		} else {
			// Check if user has an existing SDR
			if (lastCreatedSDR.data?.sdrs && lastCreatedSDR.data.sdrs.length > 0) {
				const latestSDR = lastCreatedSDR.data.sdrs[0];
				setSDRId(latestSDR.sdrId);
				setShowPrompt(false);
			}
		}
	}, [lastCreatedSDR.data]);

	// Track page views and session analytics
	useEffect(() => {
		const trackPage = async () => {
			try {
				// Generate or get session ID
				let sessionId = sessionStorage.getItem("sdr-session-id");
				if (!sessionId) {
					sessionId = crypto.randomUUID();
					sessionStorage.setItem("sdr-session-id", sessionId);
				}

				// Track page view using the hook
				// await trackPageView.mutate({
				// 	page: "homepage",
				// 	sessionId: sessionId,
				// 	timestamp: Date.now(),
				// });
			} catch (error) {
				// Silent fail for analytics
				console.log("Analytics tracking error:", error);
			}
		};

		trackPage();
	}, []); // Empty dependency array - only run once on mount

	const createSDRWithPersona = async (personaText: string) => {
		if (!personaText.trim()) {
			return;
		}

		try {
			const result = await createSDRMutation.mutate({
				persona: personaText.trim(),
			});
			if (result) {
				// Update state and refresh the last created SDR data
				setSDRId(result.sdrId);
				setShowPrompt(false);
				// Refetch the last created SDR to keep data in sync
				lastCreatedSDR.refetch();
			}
		} catch (error) {
			console.error("Error creating SDR:", error);
		}
	};

	const createSDR = async () => {
		await createSDRWithPersona(persona);
	};

	const resetSDR = () => {
		// Reset state to show prompt for creating new SDR
		setSDRId(null);
		setPersona("");
		setShowPrompt(true);
	};

	// Show dual panel if we have an SDR ID - NO HEADER
	if (sdrId && !showPrompt) {
		return <SDRDualPanel sdrId={sdrId} onBack={resetSDR} />;
	}

	// Show prompt interface with header - MODERN ANIMATED DESIGN
	return (
		<div className="min-h-dvh bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col">
			{/* UserHeader only on this page */}
			<UserHeader />
			
			{/* Main Content with top padding for fixed header */}
			<div className="flex-1 flex flex-col items-center justify-center p-6 pt-20 sm:pt-32 pb-12 sm:pb-16">
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 1, delay: 0.2 }}
					className="text-center max-w-3xl mx-auto"
				>
					<h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6 leading-tight">
						Hire a{' '}
						<span className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
							Self-Evolving AI Sales Rep
						</span>{' '}
						in 60 Seconds.
					</h1>
					
					<p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-10 leading-relaxed">
						Stop scripting chatbots. Start collaborating with an AI partner that learns, adapts, and grows into your perfect sales expert.
					</p>
					
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8, delay: 0.4 }}
						className="w-full max-w-3xl mx-auto"
					>
						<div className="mb-8">
							<h3 className="text-lg font-semibold text-white mb-4 text-center">
								Try these examples or write your own:
							</h3>
							<div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
								{SDR_INITIAL_PROMPTS.map((prompt, index) => (
									<motion.button
										key={prompt.button}
										onClick={() => setPersona(prompt.persona)}
										className="group relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 hover:from-purple-600/20 hover:to-indigo-600/20 border border-slate-600/50 hover:border-purple-500/50 rounded-lg p-4 transition-all duration-300 ease-in-out hover:shadow-lg hover:shadow-purple-500/20"
										whileHover={{ scale: 1.02, y: -2 }}
										whileTap={{ scale: 0.98 }}
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.6, delay: 0.6 + (index * 0.1) }}
									>
										<div className="text-center">
											<div className="text-white font-semibold text-sm mb-1 group-hover:text-purple-300 transition-colors">
												{prompt.button}
											</div>
											<div className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors">
												{prompt.button === "Happywood" && "Lumber Store"}
												{prompt.button === "EcoClean" && "Cleaning Service"}
												{prompt.button === "SolarMax" && "Solar Installation"}
												{prompt.button === "WealthPath" && "Investment Advisory"}
											</div>
										</div>
										<div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-indigo-500/0 group-hover:from-purple-500/10 group-hover:to-indigo-500/10 rounded-lg transition-all duration-300" />
									</motion.button>
								))}
							</div>
						</div>
						
						<label htmlFor="sdr-prompt" className="block text-sm font-medium text-gray-300 mb-2">
							Describe the job for your new AI SDR:
						</label>
						<div className="max-w-2xl mx-auto">
							<textarea
								ref={textareaRef}
								id="sdr-prompt"
								value={persona}
								onChange={(e) => setPersona(e.target.value)}
								className="w-full rounded-lg p-4 text-white placeholder-gray-500 text-base resize-none bg-white/5 border border-white/20 transition-all duration-300 ease-in-out focus:bg-white/10 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30 focus:outline-none min-h-[80px] overflow-hidden"
								placeholder="Example: You are an SDR for 'InnovateCRM'. Your goal is to book demos with founders of tech startups with 10-50 employees..."
							/>
						</div>
						<div className="text-center">
							<motion.button
								onClick={createSDR}
								className="mt-4 w-full sm:w-auto bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold py-3 px-8 rounded-lg text-lg transition-all duration-300 ease-in-out hover:shadow-lg hover:shadow-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
								whileHover={{ scale: 1.05, y: -2 }}
								whileTap={{ scale: 0.95 }}
								disabled={!persona.trim() || createSDRMutation.loading}
							>
								{createSDRMutation.loading ? (
									<span className="flex items-center gap-3">
										<div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
										Building Your AI SDR...
									</span>
								) : (
									"Build & Test My AI SDR"
								)}
							</motion.button>
						</div>
					</motion.div>
				</motion.div>

				{/* How It Works Section */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.8 }}
					className="mt-16 w-full max-w-4xl mx-auto"
				>
					<Card className="p-8 bg-gray-800/30 border-gray-700/30 backdrop-blur-sm">
						<h3 className="text-2xl font-bold text-white mb-6 text-center">
							How it works:
						</h3>
						<div className="grid md:grid-cols-3 gap-8">
							{[
								{
									number: "1",
									title: "Prompt & Deploy",
									description: "Describe your ideal sales rep. Your AI is instantly \"hired\" and ready to engage leads based on your instructions.",
									gradient: "from-indigo-500 to-indigo-600"
								},
								{
									number: "2", 
									title: "Collaborate & Guide",
									description: "When your AI faces a new question, it consults you. You provide the answer, teaching it on the fly.",
									gradient: "from-purple-500 to-purple-600"
								},
								{
									number: "3",
									title: "Learn & Evolve", 
									description: "Every piece of guidance is stored. Your AI constantly improves, turning your expertise into a scalable asset.",
									gradient: "from-pink-500 to-pink-600"
								}
							].map((step, index) => (
								<motion.div
									key={step.number}
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.6, delay: 1.0 + (index * 0.2) }}
									className="text-center"
								>
									<div className={`w-16 h-16 bg-gradient-to-r ${step.gradient} rounded-full flex items-center justify-center mx-auto mb-4`}>
										<span className="text-3xl font-bold text-white">{step.number}</span>
									</div>
									<h4 className="text-xl font-semibold text-white mb-3">
										{step.title}
									</h4>
									<p className="text-gray-300 leading-relaxed">
										{step.description}
									</p>
								</motion.div>
							))}
						</div>
					</Card>
				</motion.div>
			</div>
		</div>
	);
}
